import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Briefcase, Store, Users, LogOut, Menu, X,
  Euro, Plus, TrendingUp, CheckCircle2, Clock
} from 'lucide-react';

const CommercialDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '', email: '', password: '', address: '', city: '', category: '', description: '', phone: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, categoriesRes] = await Promise.all([
        api.get('/commercial/stats'),
        api.get('/categories')
      ]);
      setStats(statsRes.data);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    try {
      await api.post('/companies', newCompany);
      toast.success('Azienda creata con successo!');
      setNewCompanyOpen(false);
      setNewCompany({
        name: '', email: '', password: '', address: '', city: '', category: '', description: '', phone: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-amber-600 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold">{user?.name}</h1>
                <p className="text-xs text-amber-200">Area Commerciale</p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg">
                <Euro className="w-4 h-4" />
                <span className="font-medium">€{stats?.total_commission?.toFixed(2) || '0.00'}</span>
              </div>
              <Button variant="ghost" className="text-white hover:bg-amber-500" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Aziende Acquisite</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_companies || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Store className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Transazioni Generate</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_transactions || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Commissioni Totali (20%)</p>
                  <p className="text-2xl font-bold text-emerald-600">€{stats?.total_commission?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="companies" className="space-y-6">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="companies" data-testid="companies-tab">Le Mie Aziende</TabsTrigger>
          </TabsList>

          {/* Companies Tab */}
          <TabsContent value="companies">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Aziende Acquisite</CardTitle>
                <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="new-company-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuova Azienda
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Registra Nuova Azienda</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                      <div>
                        <Label>Nome Azienda *</Label>
                        <Input
                          value={newCompany.name}
                          onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                          data-testid="company-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Email *</Label>
                          <Input
                            type="email"
                            value={newCompany.email}
                            onChange={(e) => setNewCompany({ ...newCompany, email: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Password *</Label>
                          <Input
                            type="password"
                            value={newCompany.password}
                            onChange={(e) => setNewCompany({ ...newCompany, password: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Categoria *</Label>
                        <Select
                          value={newCompany.category}
                          onValueChange={(value) => setNewCompany({ ...newCompany, category: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona categoria" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Indirizzo *</Label>
                        <Input
                          value={newCompany.address}
                          onChange={(e) => setNewCompany({ ...newCompany, address: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Città *</Label>
                          <Input
                            value={newCompany.city}
                            onChange={(e) => setNewCompany({ ...newCompany, city: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Telefono</Label>
                          <Input
                            value={newCompany.phone}
                            onChange={(e) => setNewCompany({ ...newCompany, phone: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Descrizione</Label>
                        <Textarea
                          value={newCompany.description}
                          onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                        />
                      </div>
                      <Button className="w-full" onClick={handleCreateCompany}>
                        Registra Azienda
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!stats?.companies || stats.companies.length === 0 ? (
                    <div className="text-center py-12">
                      <Store className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 mb-4">Nessuna azienda acquisita</p>
                      <p className="text-sm text-slate-400">
                        Inizia ad acquisire aziende per guadagnare commissioni!
                      </p>
                    </div>
                  ) : (
                    stats.companies.map((company) => (
                      <div key={company.id} className="p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-lg">{company.name}</h4>
                            <p className="text-sm text-slate-500">{company.category}</p>
                            <p className="text-sm text-slate-400">{company.address}, {company.city}</p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <Badge className={company.registration_paid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                {company.registration_paid ? (
                                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Attiva</>
                                ) : (
                                  <><Clock className="w-3 h-3 mr-1" /> In attesa</>
                                )}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500 mt-2">
                              Borsellino: €{company.wallet_balance?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default CommercialDashboard;
